import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { AlertFlashService, UiBtnDirective } from '@callrail/looky/ui';
import {
  LoginFlow,
  MigrationService,
} from '../../services/migration/migration.service';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  email,
  form,
  FormField,
  minLength,
  required,
} from '@angular/forms/signals';
import { CodeInputComponent } from '../code-input/code-input.component';
import { FormsModule } from '@angular/forms';
import {
  CognitoAuthService,
  CognitoSignInResult,
} from '../../services/cognito-auth/cognito-auth.service';
import { PostLoginService } from '../../services/post-login/post-login.service';
import { StytchMfa, StytchService } from '../../services/stytch/stytch.service';
import {
  B2BPasswordAuthenticateResponse,
  MfaRequired,
} from '@stytch/vanilla-js/b2b';

function stytchMfaMethod(
  mfaRequired: MfaRequired | null
): StytchMfa['method'] | null {
  if (mfaRequired?.secondary_auth_initiated === 'sms_otp') return 'sms';
  if (mfaRequired?.member_options?.totp_registration_id) return 'totp';
  if (mfaRequired?.member_options?.mfa_phone_number) return 'sms';
  return null;
}

@Component({
  selector: 'anbs-email-first-login',
  imports: [FormField, UiBtnDirective, CodeInputComponent, FormsModule],
  templateUrl: './email-first-login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailFirstLoginComponent {
  private alertFlash = inject(AlertFlashService);
  private migrationService = inject(MigrationService);
  private stytch = inject(StytchService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cognitoAuth = inject(CognitoAuthService);
  private postLogin = inject(PostLoginService);
  credentials = model({
    email: '',
    password: '',
  });
  credentialsForm = form(this.credentials, (creds) => {
    required(creds.email);
    email(creds.email);
    required(creds.password);
    minLength(creds.password, 6);
  });
  mfaFormModel = model({
    code: '',
  });
  mfaForm = form(this.mfaFormModel, (mfa) => {
    required(mfa.code, { message: 'is required' });
    minLength(mfa.code, 6, { message: 'must be 6 digits' });
  });
  otpContinue = viewChild<ElementRef>('otpContinue');
  formState = signal<'enterEmail' | 'login' | 'mfa'>('enterEmail');
  showPassword = signal(false);
  submitting = signal(false);
  loginFlow = signal<LoginFlow>('cognito');

  private passwordEl = viewChild<ElementRef>('password');
  private emailEl = viewChild<ElementRef>('email');
  private codeInput = viewChild(CodeInputComponent);

  private initialOrg = signal('account-center');
  private stytchMfa = signal<StytchMfa>(null);

  constructor() {
    effect(() => {
      if (this.mfaFormModel().code.length === 6) {
        this.otpContinue()?.nativeElement.focus();
        this.otpContinue()?.nativeElement.click();
      }
    });
  }

  continue() {
    switch (this.formState()) {
      case 'enterEmail':
        if (this.credentialsForm.email().invalid()) return;
        this.submitting.set(true);
        this.migrationService
          .checkEmail$(this.credentialsForm.email().value())
          .pipe(finalize(() => this.submitting.set(false)))
          .subscribe({
            next: (resp) => {
              this.loginFlow.set(resp.login_flow);
              this.formState.set('login');
              this.initialOrg.set(resp.initial_login_org);
              setTimeout(() => this.passwordEl().nativeElement.focus(), 0);
            },
            error: () => this.alertFlash.addDanger(),
          });
        break;
      case 'login':
        if (this.credentialsForm().invalid()) return;
        if (this.loginFlow() === 'stytch') {
          this.signInWithStytch();
          return;
        }
        this.signInWithCognito();
        break;
      case 'mfa':
        if (this.mfaForm().invalid()) return;
        if (this.loginFlow() === 'stytch') {
          this.confirmStytchMfa();
          return;
        }
        this.handleCognitoMFA();
        break;
    }
  }

  private signInWithCognito() {
    const credentials = this.credentials();
    this.submitting.set(true);
    this.cognitoAuth
      .signIn$(credentials.email, credentials.password)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (result) => this.handleCognitoSignIn(result),
        error: (err: { message?: string }) =>
          this.alertFlash.addDanger(err?.message),
      });
  }

  private handleCognitoSignIn(result: CognitoSignInResult) {
    switch (result.status) {
      case 'signedIn':
        this.postLogin.redirect(this.route.snapshot.queryParamMap);
        break;
      case 'mfaRequired':
        this.formState.set('mfa');
        break;
      case 'unsupportedChallenge':
        this.router.navigateByUrl(
          `/?email=${encodeURIComponent(this.credentials().email)}`,
          { replaceUrl: true }
        );
        break;
    }
  }

  private handleCognitoMFA() {
    this.submitting.set(true);
    this.cognitoAuth
      .confirmSignIn$(this.mfaForm.code().value())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (resp) => {
          this.handleCognitoSignIn(resp);
        },
        error: (err) => {
          this.alertFlash.addDanger(err?.message);
          this.mfaForm().reset({ code: '' });
          this.codeInput().otpCode()?.nativeElement.focus();
        },
      });
  }

  private signInWithStytch() {
    const credentials = this.credentials();
    this.submitting.set(true);
    this.stytch
      .signIn(credentials.email, credentials.password, this.initialOrg())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (resp) => this.handleStytchSignIn(resp),
        error: (err) => this.flashStytchError(err),
      });
  }

  private handleStytchSignIn(resp: B2BPasswordAuthenticateResponse) {
    if (resp.member_authenticated) {
      this.postLogin.redirect(this.route.snapshot.queryParamMap);
      return;
    }
    const method = stytchMfaMethod(resp.mfa_required);
    if (!method) {
      this.alertFlash.addDanger();
      return;
    }
    const mfa: StytchMfa = {
      organization_id: resp.organization.organization_id,
      member_id: resp.member.member_id,
      method,
    };
    this.stytchMfa.set(mfa);
    this.formState.set('mfa');
    if (method === 'sms' && !resp.mfa_required.secondary_auth_initiated) {
      this.stytch
        .sendSmsCode(mfa)
        .subscribe({ error: (err) => this.flashStytchError(err) });
    }
  }

  private confirmStytchMfa() {
    this.submitting.set(true);
    this.stytch
      .confirmMfa(this.stytchMfa(), this.mfaForm.code().value())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => this.postLogin.redirect(this.route.snapshot.queryParamMap),
        error: (err) => {
          this.flashStytchError(err);
          this.mfaForm().reset({ code: '' });
          this.codeInput().otpCode()?.nativeElement.focus();
        },
      });
  }

  private flashStytchError(err: { error_message?: string; message?: string }) {
    this.alertFlash.addDanger(err?.error_message ?? err?.message);
  }

  togglePassword() {
    this.showPassword.update((shown) => !shown);
  }

  goBack() {
    this.credentials.set({ email: '', password: '' });
    this.mfaFormModel.set({ code: '' });
    this.credentialsForm().reset();
    this.mfaForm().reset();
    this.formState.set('enterEmail');
    setTimeout(() => {
      this.emailEl().nativeElement.focus();
    }, 0);
  }

  resetPassword() {
    if (this.credentialsForm.email().invalid()) return;
    this.submitting.set(true);
    this.stytch
      .resetPassword(this.credentialsForm.email().value(), this.initialOrg())
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.alertFlash.addSuccess('Please check your email.');
        },
        error: (err) => {
          this.flashStytchError(err);
          this.credentialsForm().reset();
          this.formState.set('enterEmail');
        },
      });
  }
}
